import React, { forwardRef, useCallback, useId, useState, type ChangeEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  defaultLucideForInputType,
  fieldShellInnerInputClass,
  FieldShellLeadIcon,
  FieldShellLeadSlot,
  fieldShellWrapperClass,
  fieldShellWrapperErrorClass,
} from '@/components/ui/field-shell';

function leadLucideFromProps(type: string | undefined, autoComplete?: string, override?: LucideIcon): LucideIcon {
  if (override) return override;
  const t = type ?? 'text';
  const ac = (autoComplete ?? '').toLowerCase();
  if (t === 'password' || (t === 'text' && ac.includes('password'))) {
    return defaultLucideForInputType('password');
  }
  return defaultLucideForInputType(t);
}

const shellSizeRowClass: Record<'sm' | 'md' | 'lg', string> = {
  sm: '!min-h-9 gap-2 px-2 py-1.5 text-xs',
  md: '',
  lg: 'min-h-12 gap-3 px-3 py-2.5 text-base',
};

export type InputSize = keyof typeof shellSizeRowClass;

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Icône Lucide fixe à gauche (prioritaire sur l’auto selon `type` / `autoComplete`). Utile pour champs custom. */
  leadingLucide?: LucideIcon;
  /** Contenu à gauche (prioritaire sur `leadingLucide` et l’auto) — rendu dans la même pastille que la loupe. */
  leftIcon?: React.ReactNode;
  /** Contenu à droite (ex. bouton œil mot de passe) — cliquable si besoin. */
  rightIcon?: React.ReactNode;
  inputSize?: InputSize;
  /** Classes sur le conteneur colonne (label + champ + messages). */
  wrapperClassName?: string;
  /** Classes sur le `<label>` (ex. `text-xs` pour formulaires denses). */
  labelClassName?: string;
  /** Classes sur la « carte » du champ (anneau primary). */
  shellClassName?: string;
  /** Affiche un bouton effacer lorsque la valeur est non vide (types texte). */
  showClear?: boolean;
  onClear?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      labelClassName,
      shellClassName,
      label,
      error,
      helperText,
      leadingLucide,
      leftIcon,
      rightIcon,
      inputSize = 'md',
      id: externalId,
      disabled,
      type = 'text',
      autoComplete,
      value,
      defaultValue,
      onChange,
      showClear,
      onClear,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const id = externalId ?? autoId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;

    const [uncontrolled, setUncontrolled] = useState(() =>
      defaultValue != null ? String(defaultValue) : '',
    );
    const isControlled = value !== undefined;
    const str = isControlled ? String(value ?? '') : uncontrolled;
    const clearVisible =
      Boolean(showClear && str.length > 0 && !disabled && type !== 'password' && type !== 'file' && type !== 'hidden');

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) setUncontrolled(e.target.value);
        onChange?.(e);
      },
      [isControlled, onChange],
    );

    const handleClear = useCallback(() => {
      onClear?.();
      if (onChange) {
        const synthetic = { target: { value: '' } } as ChangeEvent<HTMLInputElement>;
        onChange(synthetic);
      }
      if (!isControlled) setUncontrolled('');
    }, [onChange, onClear, isControlled]);

    const dense = inputSize === 'sm';
    const AutoIcon = leadLucideFromProps(type, autoComplete, leadingLucide);

    return (
      <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
        {label && (
          <label htmlFor={id} className={cn('text-sm font-medium text-gray-700 dark:text-gray-300', labelClassName)}>
            {label}
          </label>
        )}

        <div
          className={cn(
            fieldShellWrapperClass,
            shellSizeRowClass[inputSize],
            shellClassName,
            error && fieldShellWrapperErrorClass,
            disabled && 'opacity-60',
          )}
        >
          {leftIcon != null ? (
            <FieldShellLeadSlot dense={dense}>{leftIcon}</FieldShellLeadSlot>
          ) : (
            <FieldShellLeadIcon Icon={AutoIcon} dense={dense} />
          )}
          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            aria-invalid={!!error}
            aria-describedby={
              [error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined
            }
            className={cn(fieldShellInnerInputClass(dense), className)}
            autoComplete={autoComplete}
            {...props}
          />
          {clearVisible ? (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'shrink-0 rounded-md p-1 text-gray-500 transition-colors hover:text-gray-800',
                'dark:text-gray-400 dark:hover:text-gray-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
              )}
              aria-label="Effacer"
            >
              <X className={cn(dense ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
            </button>
          ) : null}
          {rightIcon ? (
            <span className="flex shrink-0 items-center text-gray-500 dark:text-gray-400 [&_button]:pointer-events-auto">
              {rightIcon}
            </span>
          ) : null}
        </div>

        {error && (
          <p id={errorId} className="text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={helperId} className="text-xs text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
